import React from 'react';
import { C } from '../constants';
import {
  leerCentroCosto, pdfDeFactura, normalizarCentro, conteoPorNaturaleza, deNaturaleza, pesos, haceCuanto, horaCorte,
} from '../services/centroCosto';

/**
 * EL CENTRO DE COSTO 225 «05-1» DE SOLAR HUB (dueño, 25-oct-2026: «cada app debe manejar su centro de
 * costos en su respectivo admin»).
 *
 * Lo que Siigo tiene en el centro de Solar Hub, leído de ROOT, que lo barre cada hora: facturas de
 * venta, pagos recibidos, compras, comprobantes, cuentas de cobro y viáticos, con la utilidad sin IVA
 * al corte del último refresco. La cuenta la hace root; acá no se suma nada (`services/centroCosto.js`).
 *
 * 🔴 Tres estados: `undefined` = cargando · `null` = NO se pudo consultar (se dice con el motivo) ·
 *    dato = dato. Un centro en cero porque root no respondió se leería como «no vendimos nada».
 * 🔴 La factura se ve DENTRO del admin, en el visor de abajo (dueño: «todo se ve en la plataforma,
 *    ningún enlace externo»).
 */

const TOPE = 100;

function Cifra({ titulo, valor, nota, tono }) {
  const color = tono === 'neg' ? C.red : tono === 'pos' ? C.green : C.heading;
  return (
    <div style={{ background: C.dark, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px' }}>
      <div style={{ fontSize: 9, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>{titulo}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{valor}</div>
      {nota ? <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{nota}</div> : null}
    </div>
  );
}

const th = { padding: '6px 8px', textAlign: 'left', color: C.muted, borderBottom: `1px solid ${C.border}`, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 500, position: 'sticky', top: 0, background: C.card };
const td = { padding: '6px 8px', borderBottom: `1px solid ${C.border}22`, color: C.text, fontSize: 11 };

function TablaDocs({ c, naturaleza, onVer }) {
  const filas = deNaturaleza(c, naturaleza);
  const conSaldo = naturaleza === 'venta' || naturaleza === 'compra';
  const esAsiento = naturaleza === 'asiento';
  return (
    <>
      {/* Con scroll propio: una lista larga no empuja el resto de la pantalla. */}
      <div style={{ maxHeight: 440, overflow: 'auto', border: `1px solid ${C.border}`, borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Documento</th>
              <th style={th}>Fecha</th>
              <th style={th}>Concepto</th>
              {esAsiento ? <th style={th}>Cuenta</th> : null}
              <th style={{ ...th, textAlign: 'right' }}>Valor</th>
              {conSaldo ? <th style={{ ...th, textAlign: 'right' }}>Saldo</th> : null}
            </tr>
          </thead>
          <tbody>
            {filas.slice(0, TOPE).map((m, i) => (
              <tr key={`${m.documento}-${i}`}>
                <td style={{ ...td, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                  {m.documento ?? '—'}
                  {naturaleza === 'venta' && m.id ? (
                    <button type="button" onClick={() => onVer(m)} style={{ marginLeft: 8, background: 'transparent', border: 'none', color: C.teal, cursor: 'pointer', fontSize: 11, padding: 0, fontFamily: 'inherit' }}>
                      Ver documento
                    </button>
                  ) : null}
                </td>
                <td style={td}>{m.fecha ?? 'sin fecha'}</td>
                <td style={{ ...td, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.concepto ?? ''}>{m.concepto ?? '—'}</td>
                {esAsiento ? <td style={{ ...td, fontFamily: 'monospace' }}>{m.cuenta ?? '—'}{m.movimiento ? ` · ${m.movimiento}` : ''}</td> : null}
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{pesos(m.valorCentavos)}</td>
                {conSaldo ? (
                  <td style={{ ...td, textAlign: 'right' }}>
                    {m.saldoCentavos && m.saldoCentavos > 0
                      ? <span style={{ color: C.yellow, fontWeight: 700 }}>{pesos(m.saldoCentavos)}</span>
                      : <span style={{ color: C.muted }}>pagada</span>}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filas.length > TOPE ? <div style={{ fontSize: 10, color: C.muted, marginTop: 6 }}>Se muestran {TOPE} de {filas.length}.</div> : null}
    </>
  );
}

/** El visor del PDF, encima de la página. Se cierra con el botón, con Escape o tocando el fondo. */
function VisorPdf({ visor, onCerrar }) {
  React.useEffect(() => {
    const tecla = (e) => { if (e.key === 'Escape') onCerrar(); };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [onCerrar]);
  return (
    <div role="dialog" aria-modal="true" aria-label={`Factura ${visor.titulo}`} onClick={onCerrar}
      style={{ position: 'fixed', inset: 0, background: '#000a', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, width: 'min(960px, 100%)', height: 'min(90vh, 1200px)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontWeight: 700, color: C.heading, fontSize: 13 }}>Factura de venta {visor.titulo}</div>
          <button type="button" onClick={onCerrar} style={{ background: 'transparent', border: `1px solid ${C.border}`, color: C.text, borderRadius: 5, padding: '4px 10px', cursor: 'pointer', fontSize: 11 }}>Cerrar</button>
        </div>
        <div style={{ flex: 1, minHeight: 0 }}>
          {visor.url ? (
            <iframe title={`Factura ${visor.titulo}`} src={visor.url} style={{ width: '100%', height: '100%', border: 'none', borderRadius: '0 0 10px 10px', background: '#fff' }} />
          ) : visor.error ? (
            <div style={{ padding: 16, fontSize: 12, color: C.red }}>{visor.error}</div>
          ) : (
            <div style={{ padding: 16, fontSize: 12, color: C.muted }}>Trayendo el documento de Siigo…</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CentroCosto() {
  const [dato, setDato] = React.useState(undefined);
  const [error, setError] = React.useState(null);
  const [cargando, setCargando] = React.useState(false);
  const [grupo, setGrupo] = React.useState(null);
  const [visor, setVisor] = React.useState(null);
  const pedido = React.useRef(0);

  const cargar = React.useCallback(async () => {
    setCargando(true);
    try {
      const c = normalizarCentro(await leerCentroCosto());
      setDato(c);
      setError(c ? null : 'root respondió algo que no es el centro de costo');
    } catch (e) {
      setDato(null);
      setError(e?.message || 'no se pudo consultar');
    } finally {
      setCargando(false);
    }
  }, []);

  React.useEffect(() => { cargar(); }, [cargar]);

  // El blob del PDF se libera al cerrar: cada factura abierta es un archivo en memoria.
  const cerrarVisor = React.useCallback(() => {
    pedido.current += 1;
    setVisor((v) => { if (v?.url) URL.revokeObjectURL(v.url); return null; });
  }, []);

  const verDocumento = React.useCallback(async (m) => {
    const n = ++pedido.current;
    setVisor({ titulo: m.documento ?? 'Factura', url: null, error: null });
    try {
      const blob = await pdfDeFactura(m.id);
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      // Si mientras tanto se cerró o se abrió otra, esta no se pinta.
      if (n !== pedido.current) { URL.revokeObjectURL(url); return; }
      setVisor((v) => (v ? { ...v, url } : v));
    } catch (e) {
      if (n === pedido.current) setVisor((v) => (v ? { ...v, error: e?.message || 'no se pudo traer el documento' } : v));
    }
  }, []);

  const grupos = dato ? conteoPorNaturaleza(dato) : [];
  const activo = grupos.some((g) => g.naturaleza === grupo) ? grupo : grupos[0]?.naturaleza ?? null;
  const edad = dato ? haceCuanto(dato.minutosMasViejo) : null;
  const ajusteGasto = dato?.ajusteGastoCcCentavos ?? 0;
  const ajusteIngreso = dato?.ajusteIngresoCcCentavos ?? 0;

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div>
          <div style={{ fontWeight: 700, color: C.heading, fontSize: 14 }}>
            Centro de costo{dato?.centroId ? ` ${dato.centroId} «${dato.centroNombre}»` : ' Solar Hub'}
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
            Lo que Siigo tiene en el centro de Solar Hub: facturas de venta, pagos recibidos, compras y comprobantes
            {dato?.desde ? ` (${dato.desde} → ${dato.hasta})` : ''}. Lo trae root cada hora.
          </div>
        </div>
        <button type="button" onClick={cargar} disabled={cargando}
          style={{ padding: '6px 12px', background: 'transparent', color: C.teal, border: `1px solid ${C.teal}66`, borderRadius: 5, cursor: cargando ? 'wait' : 'pointer', fontSize: 11, whiteSpace: 'nowrap' }}>
          {cargando ? 'Cargando…' : 'Actualizar'}
        </button>
      </div>

      {dato === undefined ? (
        <div style={{ fontSize: 12, color: C.muted }}>Cargando el centro de costo…</div>
      ) : dato === null ? (
        <div style={{ fontSize: 12, color: C.yellow, border: `1px solid ${C.yellow}55`, borderRadius: 8, padding: '8px 12px' }}>
          No se pudo consultar el centro de costo en root ({error}). No es que no haya movimientos: vuelve a intentar en un momento.
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 9 }}>
            <Cifra
              titulo="Facturado sin IVA"
              valor={pesos(dato.ventasBaseCentavos ?? dato.ventasCentavos)}
              nota={`${pesos(dato.ventasCentavos)} con IVA · ${dato.porCobrarCentavos > 0 ? `${pesos(dato.porCobrarCentavos)} por cobrar` : 'todo cobrado'}`}
            />
            <Cifra titulo="Pagos recibidos" valor={pesos(dato.recaudosCentavos)} nota="lo que entró (no se suma a lo facturado)" />
            <Cifra
              titulo="Compras sin IVA"
              valor={pesos(dato.comprasBaseCentavos ?? dato.comprasCentavos)}
              nota={`${pesos(dato.comprasCentavos)} con IVA${dato.porPagarCentavos > 0 ? ` · ${pesos(dato.porPagarCentavos)} por pagar` : ''}`}
            />
            <Cifra titulo="Cuentas de cobro y viáticos" valor={pesos(dato.cuentasCobroCentavos + dato.viaticosCentavos)} />
            <Cifra
              titulo="Utilidad sin IVA"
              valor={dato.netoCentavos === null ? 'sin dato' : pesos(dato.netoCentavos)}
              nota={dato.netoCentavos === null ? dato.motivoSinNeto : `al corte de ${horaCorte(dato.corte) ?? '—'}`}
              tono={dato.netoCentavos === null ? undefined : dato.netoCentavos < 0 ? 'neg' : 'pos'}
            />
          </div>
          <div style={{ fontSize: 10, color: C.muted, marginTop: 8 }}>
            Datos de Siigo {edad ? `de ${edad}` : 'sin hora conocida'}.{dato.truncado ? ' La lista está recortada a los últimos 500 movimientos.' : ''}
          </div>
          {!dato.completo || dato.advertencias.length > 0 ? (
            <div style={{ fontSize: 11, color: C.yellow, border: `1px solid ${C.yellow}55`, borderRadius: 8, padding: '6px 10px', marginTop: 8 }}>
              {!dato.completo ? <div>Root avisa que el libro no está completo: las cifras pueden quedarse cortas.</div> : null}
              {dato.advertencias.map((a, i) => <div key={i}>· {a}</div>)}
            </div>
          ) : null}
          {dato.comprobantesEnAjuste > 0 ? (
            <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>
              La utilidad incluye {dato.comprobantesEnAjuste} comprobante(s) contable(s) que mueven el resultado del centro:
              {ajusteGasto !== 0 ? ` gasto ${ajusteGasto < 0 ? '−' : '+'}${pesos(Math.abs(ajusteGasto))}` : ''}
              {ajusteIngreso !== 0 ? ` · ingreso ${ajusteIngreso < 0 ? '−' : '+'}${pesos(Math.abs(ajusteIngreso))}` : ''}
              {' '}(p. ej. el reparto de una compra a otra app). Están en la pestaña «Comprobantes».
            </div>
          ) : null}

          {grupos.length === 0 ? (
            <div style={{ fontSize: 12, color: C.muted, marginTop: 14 }}>Sin movimientos en el centro en este rango.</div>
          ) : (
            <div style={{ marginTop: 14 }}>
              <div role="tablist" style={{ display: 'inline-flex', gap: 3, flexWrap: 'wrap', background: `${C.muted}14`, border: `1px solid ${C.border}`, borderRadius: 9, padding: 3, marginBottom: 10 }}>
                {grupos.map((g) => {
                  const on = g.naturaleza === activo;
                  return (
                    <button key={g.naturaleza} type="button" role="tab" aria-selected={on} onClick={() => setGrupo(g.naturaleza)}
                      style={{ padding: '5px 11px', border: 'none', borderRadius: 7, background: on ? C.teal : 'transparent', color: on ? '#fff' : C.muted, cursor: 'pointer', fontSize: 11, fontWeight: on ? 700 : 500 }}>
                      {g.etiqueta} ({g.n})
                    </button>
                  );
                })}
              </div>
              {activo ? <TablaDocs c={dato} naturaleza={activo} onVer={verDocumento} /> : null}
            </div>
          )}
        </>
      )}

      {visor ? <VisorPdf visor={visor} onCerrar={cerrarVisor} /> : null}
    </div>
  );
}
